// AccountRangeMatcher
//
// Client-side, illustrative account matching for dynamic client reports. Ported
// from the deprecated CoffeeScript (report_editor/account_range_matcher) and
// mirrors the server-side AccountRange (lib/account_range.rb) for the subset the
// editor needs: parsing a range string and testing whether an account matches.
// The server remains authoritative when computing accounts for actual report
// rendering, so the simplify/overlap parts of AccountRange are intentionally not
// ported.
//
// Exposed on the global Silverfin.ReportEditor namespace by
// behaviors/setup-account-range-matcher so the (still CoffeeScript) groups editor
// can instantiate it.

const ACCOUNT_TYPES = ['asset', 'liability', 'income', 'expense', 'equity']
const MAXIMUM_COUNT = 200
const ALNUM = /[A-Za-z0-9]/

export interface MatchableAccount {
  id?: string | number | null
  number?: string | null
  type?: string | null
}

// Mirrors Ruby's String#succ for a single character: returns the next character
// and whether the increment carried (wrapped) into the position to its left.
function incrementChar(char: string): [string, boolean] {
  if (char === '9') return ['0', true]
  if (char === 'z') return ['a', true]
  if (char === 'Z') return ['A', true]
  return [String.fromCharCode(char.charCodeAt(0) + 1), false]
}

// The leading character inserted when an increment carries past the leftmost
// position, matching Ruby ("99".succ === "100", "zz".succ === "aaa").
function carryChar(char: string): string {
  if (char >= '0' && char <= '9') return '1'
  if (char >= 'a' && char <= 'z') return 'a'
  return 'A'
}

// Faithful port of Ruby's String#succ. When the string contains any alphanumeric
// character, only alphanumerics are incremented/carried (others stay in place);
// otherwise the rightmost character is incremented by code point. This replaces
// the previous digits-only implementation, which left non-numeric tokens
// unchanged and so silently failed to expand alphanumeric ranges.
function succ(value: string): string {
  if (value.length === 0) return value

  const chars = value.split('')
  const hasAlnum = chars.some((char) => ALNUM.test(char))

  let index = chars.length - 1
  let carry = true
  let lastIncrementedIndex = -1

  while (index >= 0 && carry) {
    if (hasAlnum && !ALNUM.test(chars[index])) {
      index -= 1
      continue
    }

    const [next, carried] = incrementChar(chars[index])
    chars[index] = next
    carry = carried
    lastIncrementedIndex = index
    index -= 1
  }

  if (carry && lastIncrementedIndex >= 0) {
    chars.splice(
      lastIncrementedIndex,
      0,
      carryChar(chars[lastIncrementedIndex])
    )
  }

  return chars.join('')
}

// Mirrors Ruby's (start..stop) expansion via String#succ, including its
// termination rules: nothing when start sorts after stop, and stop once succ
// grows past the endpoint's length (e.g. ("1".."a") yields "1".."9"). Collects at
// most MAXIMUM_COUNT + 1 items so the caller can detect oversized ranges.
function expandRange(start: string, stop: string): string[] {
  if (!start || !stop) return start ? [start] : []
  if (start > stop) return []

  const result: string[] = []
  let current = start

  while (result.length <= MAXIMUM_COUNT) {
    result.push(current)
    if (current === stop) break

    const next = succ(current)
    if (next === current || next.length > stop.length) break
    current = next
  }

  return result
}

export class AccountRangeMatcher {
  static readonly MAXIMUM_COUNT = MAXIMUM_COUNT

  ids: string[] = []
  fullNumbers: string[] = []
  types: string[] = []
  prefixes: string[] = []
  tooLarge = false

  constructor(rangeString?: string | null) {
    if (rangeString) this.parse(rangeString)
  }

  parse(rangeString: string): void {
    const tokens: string[] = []

    for (const chunk of rangeString.split(',')) {
      if (!chunk) continue

      // Negative lookahead avoids splitting on the first double underscore when
      // there are three, so a trailing literal underscore is preserved.
      const [rangeStart, rangeStop] = chunk.split(/__(?!_)/)
      if (rangeStop !== undefined) {
        const expanded = expandRange(rangeStart, rangeStop)
        if (expanded.length > MAXIMUM_COUNT) {
          this.tooLarge = true
          return
        }
        tokens.push(...expanded)
      } else if (rangeStart) {
        tokens.push(rangeStart)
      }
    }

    for (const token of tokens) {
      if (!token) continue

      if (token.charAt(0) === '#') {
        this.ids.push(token.slice(1))
      } else if (token.charAt(token.length - 1) === '$') {
        this.fullNumbers.push(token.slice(0, -1))
      } else if (ACCOUNT_TYPES.includes(token)) {
        this.types.push(token)
      } else {
        this.prefixes.push(token)
      }
    }
  }

  isEmpty(): boolean {
    return (
      this.ids.length === 0 &&
      this.fullNumbers.length === 0 &&
      this.types.length === 0 &&
      this.prefixes.length === 0
    )
  }

  matches(account?: MatchableAccount | null): boolean {
    if (!account) return false

    const id =
      account.id !== null && account.id !== undefined
        ? String(account.id)
        : null
    if (id && this.ids.includes(id)) return true

    const number = account.number
    if (number && this.fullNumbers.includes(number)) return true
    if (account.type && this.types.includes(account.type)) return true
    if (number && this.prefixes.some((prefix) => number.startsWith(prefix)))
      return true

    return false
  }
}

export default AccountRangeMatcher
